// Family cook-stats — household-wide aggregate of cook_logs by dish.
//
// The viewer-scoped cousin (useRecipeCookStats) deliberately filters
// to the viewer's own logs; THIS hook leans on cook_logs' RLS
// (owner OR family-of-owner OR listed diner — migration 0013's
// "shared-kitchen feel") to read the whole household's recent cooks
// and answer "what does this family actually love?"
//
// Scoring matches the community leaderboard (migration 0236) so a
// family favorite means the same thing a community favorite does:
//
//   score = ((sumRating + 2) / (cookCount + 2)) * ln(cookCount + 1)
//   ratings: rough=0 · meh=1 · good=2 · nailed=3
//
// Bayesian prior anchors fresh dishes near "good"; the log-volume
// term keeps one lucky "nailed" from beating the carbonara the
// family cooks every week.
//
// Egress posture: ONE windowed query (90 days, ≤600 rows, 7 small
// columns) behind a 30-minute useCachedQuery TTL. The cache key
// carries a familyKey so adding/removing a family member refreshes.
//
// Slug caveat (known UUID-migration debt): dishes cluster by
// recipe_slug + title text. Two family members each owning a
// DIFFERENT recipe under the same slug would pool — acceptable for
// a favorites leaderboard; the UUID migration is the real fix.

import { useMemo } from "react";
import { supabase } from "./supabase";
import { useCachedQuery } from "./useCachedQuery";

const TTL_30M = 30 * 60 * 1000;
const WINDOW_DAYS = 90;

const RATING_VALUE = { rough: 0, meh: 1, good: 2, nailed: 3 };

async function fetchFamilyCookLogs() {
  const since = new Date(Date.now() - WINDOW_DAYS * 86400000).toISOString();
  const { data, error } = await supabase
    .from("cook_logs")
    .select("user_id, recipe_slug, recipe_title, recipe_emoji, rating, diners, cooked_at")
    .gte("cooked_at", since)
    .order("cooked_at", { ascending: false })
    .limit(600);
  if (error) {
    console.warn("[familyCookStats] fetch failed:", error.message);
    return [];
  }
  return data || [];
}

function aggregate(logs) {
  const bySlug = new Map();
  for (const log of logs) {
    if (!log?.recipe_slug) continue;
    const t = log.cooked_at ? +new Date(log.cooked_at) : 0;
    let entry = bySlug.get(log.recipe_slug);
    if (!entry) {
      entry = {
        slug: log.recipe_slug,
        title: log.recipe_title || log.recipe_slug,
        emoji: log.recipe_emoji || null,
        cookCount: 0,
        chefIds: new Set(),
        eaterIds: new Set(),
        sumRating: 0,
        ratedCount: 0,
        lastCookedAt: 0,
      };
      bySlug.set(log.recipe_slug, entry);
    }
    entry.cookCount += 1;
    if (log.user_id) {
      entry.chefIds.add(log.user_id);
      entry.eaterIds.add(log.user_id);
    }
    for (const d of Array.isArray(log.diners) ? log.diners : []) {
      if (d) entry.eaterIds.add(d);
    }
    const rv = RATING_VALUE[log.rating];
    if (rv != null) {
      entry.sumRating += rv;
      entry.ratedCount += 1;
    }
    if (t > entry.lastCookedAt) {
      entry.lastCookedAt = t;
      // Latest cook's denormalized title/emoji wins (renames flow).
      if (log.recipe_title) entry.title = log.recipe_title;
      if (log.recipe_emoji) entry.emoji = log.recipe_emoji;
    }
  }

  const favorites = [...bySlug.values()].map((e) => ({
    slug: e.slug,
    title: e.title,
    emoji: e.emoji,
    cookCount: e.cookCount,
    chefIds: [...e.chefIds],
    eaterCount: e.eaterIds.size,
    avgRating: e.ratedCount > 0 ? e.sumRating / e.ratedCount : null,
    lastCookedAt: e.lastCookedAt || null,
    score: ((e.sumRating + 2) / (e.cookCount + 2)) * Math.log(e.cookCount + 1),
  }));
  favorites.sort((a, b) => b.score - a.score);
  return { favorites, bySlug };
}

/**
 * @param {string} userId
 * @param {string} familyKey — stable key of the family roster (e.g.
 *                             familyIds.join(",")) so membership
 *                             changes bust the cache.
 * @returns {{ favorites: Array, bySlug: Map<slug, cluster>, loading, refresh }}
 */
export function useFamilyCookStats(userId, familyKey = "") {
  const { data, loading, refresh } = useCachedQuery({
    cacheKey: `family_cook_stats:${userId || "anon"}:${familyKey}`,
    ttlMs: TTL_30M,
    fetcher: fetchFamilyCookLogs,
    enabled: !!userId,
    defaultValue: [],
  });
  const { favorites, bySlug } = useMemo(() => aggregate(data || []), [data]);
  return { favorites, bySlug, loading, refresh };
}
